import {
  type QueryKey,
  useInfiniteQuery,
  type UseInfiniteQueryOptions,
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryOptions,
} from '@tanstack/react-query';
import type { AxiosError } from 'axios';

import type { PageableContent } from '@cobliteam/common-types';
import type {
  GeofenceFilter,
  GeofenceResume,
  PlainGeofence,
} from '@cobliteam/common-types/src/geofenceV3';

import GeofenceRequester from '@cobliteam/api-lib/src/dashboard/geofence/GeofenceRequesterV3';

import { Alert, AlertTypes, showAlertActionCreator } from '@cobliteam/alert';
import { Environment } from '@cobliteam/react-environments';

const env = Environment.getInstance().getEnvironment();
const geofenceRequester = new GeofenceRequester({ host: env.geofenceApiHost });
export const QUERY_KEY: QueryKey = ['GEOFENCES_V3'];

const getGeofencesLimitErrorMessage = (
  error: AxiosError<any>,
  defaultMessage: string
) => {
  // TODO: wait for backend to return a better structure for errors
  const maxGeofencesByFleetErrorMessage =
    'Total of geofences exceeded the limit by fleet';
  const maxAddressesPerRequestErrorMessage =
    'Total items per request exceeded the limit';
  const emptySpreadsheet =
    "The uploaded batch doesn't follow the specified limitations.";
  const maximumComplexity =
    'maximum complexity reached by the polygon, you must use a maximum of 40 points';

  if (
    error.response?.data?.errors?.error?.includes(
      maxGeofencesByFleetErrorMessage
    )
  ) {
    return Alert.error(
      'Limite de 5000 locais de interesse excedido. Remova um local de interesse para adicionar um novo.'
    );
  }

  if (
    error.response?.data?.errors?.error?.includes(
      maxAddressesPerRequestErrorMessage
    )
  ) {
    return Alert.error(
      'Limite de 100 locais de interesse por planilha excedido.'
    );
  }

  if (error.response?.data?.errors?.error?.includes(emptySpreadsheet)) {
    return Alert.error('Nenhum endereço foi encontrado na planilha.');
  }

  if (error.response?.data?.errors?.error?.includes(maximumComplexity)) {
    return Alert.error(
      'Falha ao salvar local. Desenhe um local de no máximo 40 pontos no mapa.'
    );
  }

  return Alert.error(defaultMessage);
};

const useInfinityGeofences = ({
  filters,
  options,
}: {
  filters: GeofenceFilter;
  options?: UseInfiniteQueryOptions<PageableContent<PlainGeofence>>;
}) => {
  return useInfiniteQuery<PageableContent<PlainGeofence>>({
    queryKey: [...QUERY_KEY, 'LIST', filters],
    queryFn: ({ pageParam = 0 }) =>
      geofenceRequester.getGeofences({
        // Concating sort by id to avoid order by instability while querying
        filters: {
          ...filters,
          page: pageParam,
          sort: filters.sort?.concat({ column: 'id', order: 'asc' }),
        },
      }),
    getNextPageParam: (lastPage, pages) => {
      if (lastPage.last === false) return pages.length;
      return undefined;
    },
    ...options,
  });
};

const useGetGeofenceById = (
  id: string,
  options?: UseQueryOptions<PlainGeofence>
) => {
  return useQuery<PlainGeofence>({
    queryKey: [...QUERY_KEY, id],
    queryFn: () => geofenceRequester.getGeofenceById(id),
    ...options,
    onError: () => {
      showAlertActionCreator(
        AlertTypes.ERROR,
        'Falha ao carregar local de interesse. Tente novamente mais tarde.'
      );
    },
  });
};

const useGetGeofenceResume = (options?: UseQueryOptions<GeofenceResume[]>) => {
  return useQuery<GeofenceResume[]>({
    queryKey: [...QUERY_KEY, 'MAP_POINTS'],
    queryFn: () => geofenceRequester.getGeofenceResume(),
    onError: () => {
      showAlertActionCreator(
        AlertTypes.ERROR,
        'Falha ao carregar locais de interesse no mapa. Tente novamente mais tarde.'
      );
    },
    ...options,
  });
};

const useCreateGeofence = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: geofenceRequester.createGeofence,
    onMutate: (newGeofence) => {
      queryClient.cancelQueries({
        queryKey: [...QUERY_KEY, 'MAP_POINTS'],
      });
      queryClient.cancelQueries({ queryKey: [...QUERY_KEY, 'LIST'] });

      // add item  to geofence resume cache on map
      queryClient.setQueryData(
        [...QUERY_KEY, 'MAP_POINTS'],
        (old: GeofenceResume[] | undefined) => {
          if (!old) return old;
          return [
            ...old,
            {
              id: 'new', // temp id to show the new geofence on map before the request finish
              lat: newGeofence.geometryPoint.coordinates[1],
              lng: newGeofence.geometryPoint.coordinates[0],
              type: newGeofence.type,
            },
          ];
        }
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [...QUERY_KEY, 'MAP_POINTS'],
      });
      queryClient.invalidateQueries({ queryKey: [...QUERY_KEY, 'LIST'] });
      Alert.success('Local de interesse criado com sucesso!');
    },
    onError: (error: AxiosError) => {
      getGeofencesLimitErrorMessage(
        error,
        'Ops! Falha ao criar local de interesse. Por favor, tente novamente mais tarde.'
      );
      throw new Error(error.message);
    },
  });
};

const useUpdateGeofence = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: geofenceRequester.updateGeofence,
    onMutate: async (plainGeofence) => {
      queryClient.cancelQueries({
        queryKey: [...QUERY_KEY, 'MAP_POINTS'],
      });
      queryClient.cancelQueries({ queryKey: [...QUERY_KEY, 'LIST'] });
      queryClient.cancelQueries({
        queryKey: [...QUERY_KEY, plainGeofence.id],
      });

      // update geofence resume cache on map
      const previousGeofenceMapPoints = queryClient.getQueryData([
        ...QUERY_KEY,
        'MAP_POINTS',
      ]);
      queryClient.setQueryData(
        [...QUERY_KEY, 'MAP_POINTS'],
        (old: GeofenceResume[] | undefined) => {
          if (!old) return old;
          return old.map((geofence) => {
            if (geofence.id === plainGeofence.id) {
              return {
                id: plainGeofence.id,
                lat: plainGeofence.geometryPoint.coordinates[1],
                lng: plainGeofence.geometryPoint.coordinates[0],
                type: plainGeofence.type,
              };
            }
            return geofence;
          });
        }
      );

      // update geofence id cache
      const previousGeofence: any = queryClient.getQueryData([
        ...QUERY_KEY,
        plainGeofence.id,
      ]);

      const previousList: PageableContent<PlainGeofence> | undefined =
        queryClient.getQueryData([...QUERY_KEY, 'LIST']);
      return {
        previousList,
        plainGeofence,
        previousGeofence,
        previousGeofenceMapPoints,
      };
    },
    onSuccess: (err, data, context: any) => {
      Alert.success('Local de interesse atualizado!');
      queryClient.invalidateQueries({
        queryKey: [...QUERY_KEY, context?.previousGeofence.id],
      });
      queryClient.invalidateQueries({ queryKey: [...QUERY_KEY, 'LIST'] });
      queryClient.setQueryData([...QUERY_KEY, context?.plainGeofence.id], {
        ...context?.previousGeofence,
        ...context?.plainGeofence,
      });
    },
    onError: (err: AxiosError, variables, context: any) => {
      getGeofencesLimitErrorMessage(
        err,
        'Erro ao tentar atualizar um local de interesse!'
      );
      queryClient.setQueryData(
        [...QUERY_KEY, context?.previousGeofence.id],
        context?.previousGeofence
      );
      queryClient.setQueryData(
        [...QUERY_KEY, 'MAP_POINTS'],
        context?.previousGeofenceMapPoints
      );
      queryClient.setQueryData([...QUERY_KEY, 'LIST'], context?.previousList);
    },
  });
};

const useDeleteGeofence = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: geofenceRequester.deleteGeofence,
    onMutate: async (geofenceId) => {
      await queryClient.cancelQueries({
        queryKey: [...QUERY_KEY, 'MAP_POINTS'],
      });
      await queryClient.cancelQueries({
        queryKey: [...QUERY_KEY, 'LIST'],
      });
      await queryClient.cancelQueries({
        queryKey: [...QUERY_KEY, geofenceId],
      });

      // remove geofence from cache
      const previousGeofencesResume: GeofenceResume[] | undefined =
        queryClient.getQueryData([...QUERY_KEY, 'MAP_POINTS']);
      queryClient.setQueryData(
        [...QUERY_KEY, 'MAP_POINTS'],
        (old: GeofenceResume[] | undefined) => {
          if (!old) return old;
          return old.filter((geofence) => geofence.id !== geofenceId);
        }
      );

      return { previousGeofencesResume };
    },
    onSuccess: () => {
      Alert.success('Local de interesse removido!');
    },
    onError: (err, variables, context: any) => {
      Alert.error(
        'Ops! Houve um erro ao deletar o local de interesse. Vamos atualizar a lista e tente novamente.'
      );
      queryClient.setQueryData(
        [...QUERY_KEY, 'MAP_POINTS'],
        context?.previousGeofencesResume
      );
    },
    onSettled: () => {
      queryClient.invalidateQueries([...QUERY_KEY, 'LIST']);
    },
  });
};

const useValidateImportGeofences = () => {
  return useMutation({
    mutationFn: geofenceRequester.validateImportGeofences,
    onError: (error: AxiosError) =>
      getGeofencesLimitErrorMessage(
        error,
        'Ops! Falha ao criar local de interesse. Por favor, tente novamente mais tarde.'
      ),
  });
};

const useImportGeofences = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: geofenceRequester.importGeofences,
    onSuccess: () => {
      Alert.success('Locais de interesse importados com sucesso!');
      queryClient.invalidateQueries([...QUERY_KEY, 'LIST']);
      queryClient.invalidateQueries([...QUERY_KEY, 'MAP_POINTS']);
    },
    onError: () =>
      Alert.error(
        'Ops! Houve um erro ao importar os endereços da planilha. Por favor, tente novamente.'
      ),
  });
};

export {
  useInfinityGeofences,
  useGetGeofenceById,
  useGetGeofenceResume,
  useCreateGeofence,
  useUpdateGeofence,
  useDeleteGeofence,
  useValidateImportGeofences,
  useImportGeofences,
};
